import React, { useEffect, useMemo, useState } from 'react';
import { ArrowDownUpIcon, ChevronLeftIcon, ChevronRightIcon, PlusIcon, SearchIcon } from 'lucide-react';
import type { AdminProfile } from '../../hooks/useAdminAccess';
import { CrmRecord, INTEREST_LABELS, STATUS_LABELS, formatCurrency, prospectId, value } from '../../lib/crm';
import { loadDailyActionQueue, type DailyAction } from '../../lib/workflow';
import { FollowUpService } from '../../lib/supabase/services/followUps.service';
import { ProspectService } from '../../lib/supabase/services/prospects.service';
interface ProspectsPageProps {
  profile: AdminProfile;
}
const pageSize = 12;
export function ProspectsPage({
  profile
}: ProspectsPageProps) {
  const [rows, setRows] = useState<CrmRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('');
  const [niche, setNiche] = useState('');
  const [source, setSource] = useState('');
  const [responsible, setResponsible] = useState('');
  const [interest, setInterest] = useState('');
  const [sort, setSort] = useState('priority');
  const [pendingFollowUps, setPendingFollowUps] = useState<CrmRecord[]>([]);
  const [workflowActions, setWorkflowActions] = useState<DailyAction[]>([]);
  const [page, setPage] = useState(1);
  useEffect(() => {
    void load();
  }, []);
  async function load() {
    setLoading(true);
    setError(null);
    try {
      const [prospects, pending, actions] = await Promise.all([ProspectService.list(), FollowUpService.listPending(), loadDailyActionQueue()]);
      setRows(prospects as CrmRecord[]);
      setPendingFollowUps(pending as CrmRecord[]);
      setWorkflowActions(actions);
    } catch {
      setError('No fue posible cargar la mesa de trabajo comercial con tus permisos actuales.');
    } finally {
      setLoading(false);
    }
  }
  const options = useMemo(() => ({
    niches: unique(rows, 'niche_name', 'niche'),
    sources: unique(rows, 'source_name', 'source'),
    responsible: unique(rows, 'assigned_name', 'responsible_name', 'assigned_to_name')
  }), [rows]);
  const pendingByProspect = useMemo(() => new Map(pendingFollowUps.map(followUp => [prospectId(followUp), followUp] as const).filter(([id]) => Boolean(id))), [pendingFollowUps]);
  const workflowByProspect = useMemo(() => new Map(workflowActions.filter(isActiveWorkflowAction).map(action => [action.prospectId, action] as const)), [workflowActions]);
  const filtered = useMemo(() => {
    const normalized = query.toLowerCase().trim();
    const list = rows.filter(row => {
      const haystack = [value(row, 'business_name'), value(row, 'contact_name'), value(row, 'whatsapp'), value(row, 'email')].join(' ').toLowerCase();
      return (!normalized || haystack.includes(normalized)) && (!status || value(row, 'status') === status) && (!niche || value(row, 'niche_name', 'niche') === niche) && (!source || value(row, 'source_name', 'source') === source) && (!responsible || value(row, 'assigned_name', 'responsible_name', 'assigned_to_name') === responsible) && (!interest || value(row, 'interest_level') === interest);
    });
    if (sort !== 'priority') {
      return [...list].sort((a, b) => value(a, sort).localeCompare(value(b, sort), 'es'));
    }
    const now = new Date();
    return list.map((row, index) => ({
      row,
      index
    })).sort((a, b) => {
      const aTiming = nextContactFor(a.row, pendingByProspect, workflowByProspect, now);
      const bTiming = nextContactFor(b.row, pendingByProspect, workflowByProspect, now);
      if (aTiming.group !== bTiming.group) {
        return aTiming.group - bTiming.group;
      }
      if (aTiming.timestamp !== bTiming.timestamp) {
        return aTiming.timestamp - bTiming.timestamp;
      }
      return a.index - b.index;
    }).map(({
      row
    }) => row);
  }, [rows, query, status, niche, source, responsible, interest, sort, pendingByProspect, workflowByProspect]);
  const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const visible = filtered.slice((page - 1) * pageSize, page * pageSize);
  useEffect(() => setPage(1), [query, status, niche, source, responsible, interest, sort]);
  return <div className="mx-auto max-w-7xl px-4 py-7 sm:px-6 lg:px-8" data-id="element-489">
      <header className="flex flex-col gap-4 border-b border-white/10 pb-6 sm:flex-row sm:items-end sm:justify-between" data-id="element-490">
        <div data-id="element-491">
          <p className="font-orbitron text-xs font-bold tracking-[.18em] text-cyan-200" data-id="element-492">
            CRM COMERCIAL
          </p>
          <h1 className="mt-2 text-3xl font-bold text-white" data-id="element-493">Prospectos</h1>
          <p className="mt-2 text-sm text-slate-400" data-id="element-494">
            Gestioná oportunidades reales registradas en TITÁN OS.
          </p>
        </div>
        <a href="/admin/prospectos/nuevo" className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-cyan-300 px-4 text-sm font-bold text-[#06202a]" data-id="element-495">
          <PlusIcon className="h-4 w-4" data-id="element-496" /> Nuevo prospecto
        </a>
      </header>

      <section className="mt-6 rounded-2xl border border-white/10 bg-[#0b1728] p-4" data-id="element-497">
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3" data-id="element-498">
          <label className="relative md:col-span-2 xl:col-span-1" data-id="element-499">
            <span className="sr-only" data-id="element-500">Buscar</span>
            <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" data-id="element-501" />
            <input value={query} onChange={e => setQuery(e.target.value)} className="min-h-11 w-full rounded-xl border border-white/10 bg-[#07111f] pl-10 pr-3 text-sm text-white" placeholder="Buscar negocio, contacto, WhatsApp o correo" data-id="element-502" />
          </label>
          <Filter label="Estado" value={status} onChange={setStatus} options={Object.entries(STATUS_LABELS).map(([id, label]) => ({
          id,
          label
        }))} data-id="element-503" />
          <Filter label="Nicho" value={niche} onChange={setNiche} options={options.niches} data-id="element-504" />
          <Filter label="Fuente" value={source} onChange={setSource} options={options.sources} data-id="element-505" />
          <Filter label="Responsable" value={responsible} onChange={setResponsible} options={options.responsible} data-id="element-506" />
          <Filter label="Interés" value={interest} onChange={setInterest} options={Object.entries(INTEREST_LABELS).map(([id, label]) => ({
          id,
          label
        }))} data-id="element-507" />
          <label className="relative" data-id="element-508">
            <ArrowDownUpIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" data-id="element-509" />
            <select value={sort} onChange={e => setSort(e.target.value)} className="min-h-11 w-full rounded-xl border border-white/10 bg-[#07111f] pl-10 pr-3 text-sm text-white" data-id="element-510">
              <option value="priority" data-id="element-511">Ordenar: prioridad comercial</option>
              <option value="business_name" data-id="element-512">Ordenar: negocio</option>
              <option value="estimated_value" data-id="element-513">Ordenar: valor estimado</option>
            </select>
          </label>
        </div>
      </section>

      {error && <p className="mt-5 rounded-xl border border-red-400/20 bg-red-400/10 p-3 text-sm text-red-100" data-id="element-514">
          {error}
        </p>}
      {loading ? <p className="mt-8 text-sm text-slate-400" data-id="element-515">Cargando prospectos…</p> : filtered.length === 0 ? <EmptyProspects data-id="element-516" /> : <>
          <div aria-label="Tabla de prospectos" className="mt-5 hidden w-full overflow-x-auto rounded-2xl border border-white/10 lg:block" role="region" tabIndex={0} data-id="element-517">
            <table className="min-w-[1750px] text-left text-sm" data-id="element-518">
              <thead className="sticky top-0 z-10 bg-[#0b1728] text-xs uppercase tracking-wide text-slate-400" data-id="element-519">
                <tr data-id="element-520">
                  {['Negocio', 'Nicho', 'Ciudad', 'Contacto', 'WhatsApp', 'Fuente', 'Responsable', 'Estado', 'Interés', 'Próxima acción', 'Próximo seguimiento', 'Valor', ''].map(heading => <th key={heading} className="px-4 py-3 font-semibold" data-id="element-521">
                      {heading}
                    </th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 bg-[#07111f]" data-id="element-522">
                {visible.map((row, index) => <ProspectRow key={prospectId(row) || index} row={row} timing={nextContactFor(row, pendingByProspect, workflowByProspect)} workflowAction={workflowByProspect.get(prospectId(row))} hasPendingFollowUp={pendingByProspect.has(prospectId(row))} data-id="element-523" />)}
              </tbody>
            </table>
          </div>
          <div className="mt-5 grid gap-3 lg:hidden" data-id="element-524">
            {visible.map((row, index) => <ProspectCard key={prospectId(row) || index} row={row} timing={nextContactFor(row, pendingByProspect, workflowByProspect)} workflowAction={workflowByProspect.get(prospectId(row))} hasPendingFollowUp={pendingByProspect.has(prospectId(row))} data-id="element-525" />)}
          </div>
          <Pagination page={page} pages={pages} onChange={setPage} total={filtered.length} data-id="element-526" />
        </>}
    </div>;
}
function ProspectRow({
  row,
  timing,
  workflowAction,
  hasPendingFollowUp
}: {
  row: CrmRecord;
  timing: ContactTiming;
  workflowAction?: DailyAction;
  hasPendingFollowUp: boolean;
}) {
  const id = prospectId(row);
  const action = nextActionLabel(row, workflowAction, hasPendingFollowUp);
  return <tr className="text-slate-300" data-id="element-527">
      <td className="max-w-[180px] px-4 py-3 font-semibold text-white" data-id="element-528">
        <a href={`/admin/prospectos/${id}`} className="hover:text-cyan-200" data-id="element-529">
          {value(row, 'business_name') || 'Sin nombre'}
        </a>
      </td>
      <td className="px-4 py-3" data-id="element-530">{value(row, 'niche_name', 'niche') || '—'}</td>
      <td className="px-4 py-3" data-id="element-531">{value(row, 'city') || '—'}</td>
      <td className="px-4 py-3" data-id="element-532">{value(row, 'contact_name') || '—'}</td>
      <td className="px-4 py-3" data-id="element-533">{value(row, 'whatsapp') || '—'}</td>
      <td className="px-4 py-3" data-id="element-534">
        {value(row, 'source_name', 'source') || '—'}
      </td>
      <td className="px-4 py-3" data-id="element-535">
        {value(row, 'assigned_name', 'responsible_name') || '—'}
      </td>
      <td className="px-4 py-3" data-id="element-536">
        <Badge value={value(row, 'status')} labels={STATUS_LABELS} data-id="element-537" />
      </td>
      <td className="px-4 py-3" data-id="element-538">
        <Badge value={value(row, 'interest_level')} labels={INTEREST_LABELS} data-id="element-539" />
      </td>
      <td className="min-w-[156px] px-4 py-3 font-semibold text-slate-100" data-id="element-540">
        {action}
      </td>
      <td className="min-w-[170px] px-4 py-3" data-id="element-541">
        <NextContact timing={timing} data-id="element-542" />
      </td>
      <td className="px-4 py-3 whitespace-nowrap" data-id="element-543">
        {formatCurrency(Number(value(row, 'estimated_value')) || null, value(row, 'currency') || 'CRC')}
      </td>
      <td className="px-4 py-3" data-id="element-544">
        <a href={`/admin/prospectos/${id}`} className="text-xs font-semibold text-cyan-200" data-id="element-545">
          Abrir
        </a>
      </td>
    </tr>;
}
function ProspectCard({
  row,
  timing,
  workflowAction,
  hasPendingFollowUp
}: {
  row: CrmRecord;
  timing: ContactTiming;
  workflowAction?: DailyAction;
  hasPendingFollowUp: boolean;
}) {
  const id = prospectId(row);
  return <article className="rounded-2xl border border-white/10 bg-[#0b1728] p-4" data-id="element-546">
      <div className="flex items-start justify-between gap-3" data-id="element-547">
        <div className="min-w-0" data-id="element-548">
          <h2 className="truncate font-bold text-white" data-id="element-549">
            {value(row, 'business_name') || 'Sin nombre'}
          </h2>
          <p className="mt-1 text-sm text-slate-400" data-id="element-550">
            {value(row, 'contact_name') || 'Contacto no disponible'} ·{' '}
            {value(row, 'city') || 'Sin ciudad'}
          </p>
        </div>
        <Badge value={value(row, 'status')} labels={STATUS_LABELS} data-id="element-551" />
      </div>
      <dl className="mt-4 grid grid-cols-2 gap-3 text-sm" data-id="element-552">
        <Item label="Próxima acción" value={nextActionLabel(row, workflowAction, hasPendingFollowUp)} data-id="element-553" />
        <div data-id="element-554">
          <dt className="text-xs text-slate-500" data-id="element-555">Próximo seguimiento</dt>
          <dd className="mt-1" data-id="element-556">
            <NextContact timing={timing} data-id="element-557" />
          </dd>
        </div>
        <Item label="Responsable" value={value(row, 'assigned_name', 'responsible_name') || '—'} data-id="element-558" />
        <Item label="Interés" value={INTEREST_LABELS[value(row, 'interest_level')] || '—'} data-id="element-559" />
      </dl>
      <a href={`/admin/prospectos/${id}`} className="mt-4 inline-flex min-h-10 items-center text-sm font-semibold text-cyan-200" data-id="element-560">
        Abrir prospecto
      </a>
    </article>;
}
function Filter({
  label,
  value,
  onChange,
  options
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: {
    id: string;
    label: string;
  }[];
}) {
  return <label data-id="element-561">
      <span className="sr-only" data-id="element-562">{label}</span>
      <select value={value} onChange={e => onChange(e.target.value)} className="min-h-11 w-full rounded-xl border border-white/10 bg-[#07111f] px-3 text-sm text-white" data-id="element-563">
        <option value="" data-id="element-564">{label}: todos</option>
        {options.map(option => <option key={option.id} value={option.id} data-id="element-565">
            {option.label}
          </option>)}
      </select>
    </label>;
}
function Badge({
  value: status,
  labels
}: {
  value: string;
  labels: Record<string, string>;
}) {
  return <span className="inline-flex whitespace-nowrap rounded-full border border-cyan-300/20 bg-cyan-300/10 px-2.5 py-1 text-xs font-semibold text-cyan-100" data-id="element-566">
      {labels[status] || status || '—'}
    </span>;
}
function Item({
  label,
  value: itemValue
}: {
  label: string;
  value: string;
}) {
  return <div data-id="element-567">
      <dt className="text-xs text-slate-500" data-id="element-568">{label}</dt>
      <dd className="mt-1 truncate text-slate-200" data-id="element-569">{itemValue}</dd>
    </div>;
}
type ContactTiming = {
  date: string | null;
  group: 0 | 1 | 2 | 3;
  label: string;
  tone: 'overdue' | 'today' | 'tomorrow' | 'upcoming' | 'unscheduled';
  timestamp: number;
};
const workflowActionLabels: Record<string, string> = {
  complete_information: 'Enviar primer mensaje',
  select_channel: 'Enviar primer mensaje',
  send_message: 'Enviar primer mensaje',
  wait_for_response: 'Esperar respuesta',
  follow_up: 'Enviar seguimiento',
  respond: 'Registrar respuesta',
  qualify: 'Registrar respuesta',
  send_proposal: 'Enviar propuesta',
  negotiate: 'Negociar',
  confirm_sale: 'Cerrar venta'
};
function isActiveWorkflowAction(action: DailyAction) {
  return action.prospectId && !action.isBlocked && !['completed', 'cancelled', 'blocked'].includes(action.workflowState);
}
function nextContactFor(row: CrmRecord, pendingByProspect: Map<string, CrmRecord>, workflowByProspect: Map<string, DailyAction>, now = new Date()): ContactTiming {
  const id = prospectId(row);
  const pending = pendingByProspect.get(id);
  const workflow = workflowByProspect.get(id);
  const date = value(pending ?? {}, 'scheduled_at') || workflow?.dueAt || null;
  const parsed = date ? new Date(date) : null;
  if (!parsed || Number.isNaN(parsed.getTime())) {
    return {
      date: null,
      group: 3,
      label: 'Sin programar',
      tone: 'unscheduled',
      timestamp: Number.POSITIVE_INFINITY
    };
  }
  const timestamp = parsed.getTime();
  if (timestamp < now.getTime()) {
    return {
      date,
      group: 0,
      label: overdueLabel(timestamp, now.getTime()),
      tone: 'overdue',
      timestamp
    };
  }
  if (isSameCalendarDay(parsed, now)) {
    return {
      date,
      group: 1,
      label: `Hoy ${formatTime(parsed)}`,
      tone: 'today',
      timestamp
    };
  }
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);
  if (isSameCalendarDay(parsed, tomorrow)) {
    return {
      date,
      group: 2,
      label: `Mañana ${formatTime(parsed)}`,
      tone: 'tomorrow',
      timestamp
    };
  }
  return {
    date,
    group: 2,
    label: `${formatDay(parsed)} ${formatTime(parsed)}`,
    tone: 'upcoming',
    timestamp
  };
}
function nextActionLabel(row: CrmRecord, workflowAction: DailyAction | undefined, hasPendingFollowUp: boolean) {
  if (hasPendingFollowUp) return 'Enviar seguimiento';
  const actionLabel = workflowAction ? workflowActionLabels[workflowAction.actionType] : null;
  if (actionLabel) return actionLabel;
  if (workflowAction?.workflowState === 'waiting') return 'Esperar respuesta';
  const status = value(row, 'status').toLowerCase();
  if (['new', 'contacted'].includes(status)) return 'Enviar primer mensaje';
  if (['follow_up', 'meeting_scheduled'].includes(status)) return 'Enviar seguimiento';
  if (status === 'responded') return 'Registrar respuesta';
  if (status === 'proposal_sent') return 'Negociar';
  if (['negotiation', 'won'].includes(status)) return 'Cerrar venta';
  return 'Registrar respuesta';
}
function NextContact({
  timing
}: {
  timing: ContactTiming;
}) {
  const toneClass = {
    overdue: 'border-rose-400/25 bg-rose-400/10 text-rose-100',
    today: 'border-orange-300/25 bg-orange-300/10 text-orange-100',
    tomorrow: 'border-amber-300/25 bg-amber-300/10 text-amber-100',
    upcoming: 'border-emerald-300/25 bg-emerald-300/10 text-emerald-100',
    unscheduled: 'border-white/10 bg-white/[0.03] text-slate-400'
  }[timing.tone];
  const indicatorClass = {
    overdue: 'bg-rose-400',
    today: 'bg-orange-300',
    tomorrow: 'bg-amber-300',
    upcoming: 'bg-emerald-300',
    unscheduled: 'bg-slate-500'
  }[timing.tone];
  return <span aria-label={`Próximo seguimiento: ${timing.label}`} className={`inline-flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-xs font-semibold ${toneClass}`} data-id="element-570">
      <span aria-hidden="true" className={`h-2 w-2 shrink-0 rounded-full ${indicatorClass}`} data-id="element-571" />
      <time dateTime={timing.date ?? undefined} data-id="element-572">{timing.label}</time>
    </span>;
}
function overdueLabel(timestamp: number, now: number) {
  const elapsedHours = Math.max(1, Math.floor((now - timestamp) / 3600000));
  if (elapsedHours < 24) {
    return `Vencido hace ${elapsedHours} ${elapsedHours === 1 ? 'hora' : 'horas'}`;
  }
  const elapsedDays = Math.floor(elapsedHours / 24);
  return `Vencido hace ${elapsedDays} ${elapsedDays === 1 ? 'día' : 'días'}`;
}
function isSameCalendarDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
function formatTime(date: Date) {
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}
function formatDay(date: Date) {
  return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
}
function unique(rows: CrmRecord[], ...keys: string[]) {
  return [...new Set(rows.map(row => value(row, ...keys)).filter(Boolean))].map(id => ({
    id,
    label: id
  }));
}
function EmptyProspects() {
  return <section className="mt-6 rounded-2xl border border-dashed border-white/15 bg-[#0b1728] p-8 text-center" data-id="element-573">
      <h2 className="text-lg font-bold text-white" data-id="element-574">
        No hay prospectos registrados todavía.
      </h2>
      <a href="/admin/prospectos/nuevo" className="mt-4 inline-flex min-h-11 items-center rounded-xl bg-cyan-300 px-4 text-sm font-bold text-[#06202a]" data-id="element-575">
        Crear primer prospecto
      </a>
    </section>;
}
function Pagination({
  page,
  pages,
  total,
  onChange
}: {
  page: number;
  pages: number;
  total: number;
  onChange: (page: number) => void;
}) {
  return <div className="mt-5 flex items-center justify-between gap-3 text-sm text-slate-400" data-id="element-576">
      <span data-id="element-577">{total} prospectos</span>
      <div className="flex items-center gap-2" data-id="element-578">
        <button disabled={page === 1} onClick={() => onChange(page - 1)} className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 disabled:opacity-40" data-id="element-579">
          <ChevronLeftIcon className="h-4 w-4" data-id="element-580" />
        </button>
        <span data-id="element-581">
          {page} / {pages}
        </span>
        <button disabled={page === pages} onClick={() => onChange(page + 1)} className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 disabled:opacity-40" data-id="element-582">
          <ChevronRightIcon className="h-4 w-4" data-id="element-583" />
        </button>
      </div>
    </div>;
}